import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CollectionImage } from '~/components/collections';
import {
	StyledContainer,
	StyledImageWrapper,
	StyledProgressIndicator,
	StyledSliderContainer,
	StyledSliderDot,
	StyledSlides,
} from './Image.styled';

const SLIDE_INTERVAL = 100;
const SLIDE_PROGRESS_STEP = 0.02;

const Image = ({ ids }: { ids: string[] }) => {
	const slides = useMemo(
		() => ids.filter((id): id is string => Boolean(id)),
		[ids],
	);
	const [currentSlide, setCurrentSlide] = useState(0);
	const progressRef = useRef(0);
	const indicatorRefs = useRef<Array<HTMLDivElement | null>>([]);

	if (indicatorRefs.current.length !== slides.length) {
		indicatorRefs.current.length = slides.length;
	}

	const resetIndicators = useCallback(() => {
		for (const el of indicatorRefs.current) {
			if (el) {
				el.style.transform = 'scaleX(0)';
			}
		}
	}, []);

	const handleSlideChange = useCallback(
		(index: number) => {
			if (index === currentSlide || index < 0 || index >= slides.length) {
				return;
			}

			resetIndicators();
			progressRef.current = 0;
			setCurrentSlide(index);
		},
		[currentSlide, resetIndicators, slides.length],
	);

	useEffect(() => {
		if (slides.length <= 1) {
			return;
		}

		resetIndicators();
		progressRef.current = 0;

		const intervalId = setInterval(() => {
			const indicator = indicatorRefs.current[currentSlide];

			if (!indicator) {
				return;
			}

			if (progressRef.current >= 1) {
				indicator.style.transform = 'scaleX(1)';
				setCurrentSlide((prevIndex) => (prevIndex + 1) % slides.length);
				progressRef.current = 0;
				return;
			}

			progressRef.current = Math.min(
				1,
				progressRef.current + SLIDE_PROGRESS_STEP,
			);
			indicator.style.transform = `scaleX(${progressRef.current})`;
		}, SLIDE_INTERVAL);

		return () => clearInterval(intervalId);
	}, [currentSlide, resetIndicators, slides.length]);

	if (!slides.length) {
		return null;
	}

	return (
		<StyledContainer>
			<StyledSlides>
				{slides.map((image, index) => (
					<StyledImageWrapper
						key={`${image}-${index}`}
						$active={currentSlide === index}
					>
						<CollectionImage
							collection="helpModals"
							id={image}
							size="default"
							objectFit="cover"
						/>
					</StyledImageWrapper>
				))}
			</StyledSlides>
			{slides.length > 1 ? (
				<StyledSliderContainer>
					{slides.map((_, index) => (
						<StyledSliderDot
							key={`dot-${index}`}
							$active={currentSlide === index}
							onClick={() => handleSlideChange(index)}
							type="button"
						>
							<StyledProgressIndicator
								ref={(el) => {
									indicatorRefs.current[index] = el;
								}}
							/>
						</StyledSliderDot>
					))}
				</StyledSliderContainer>
			) : null}
		</StyledContainer>
	);
};

export { Image };
